<!--
@component
Standings

See markup below.
-->

<script lang="ts">
  import { appState } from '$lib/stores/index.svelte';
  import { Block } from '@reuters-graphics/graphics-components';
  import { fly } from 'svelte/transition';
  import TeamCard from '$lib/components/common/TeamCard/index.svelte';
  import TeamCardPlaceholder from '$lib/components/common/TeamCard/Placeholder.svelte';
  import LazyMount from '$lib/components/common/LazyMount/index.svelte';
  import { getGroups } from '$utils/getGroups';

  /** Reserved height for off-screen TeamCard rows (matches rendered size). */
  const TEAM_CARD_HEIGHT = 'calc(4 * var(--theme-font-size-base))';

  /** Number of recent results to surface from the API's `lastSix` string. */
  const RECENT_RESULTS_COUNT = 3;

  /* Matches data from API */
  let { data } = $props();

  /**
   * Calculates the standings for each group based on the provided match data.
   */
  const groupdata = $derived(getGroups(data.matches));

  /**
   * Calculates the team statistics for each group in the standings.
   *
   * Each team includes: groupName, name, code, rank, points, qualified,
   * slug, shortName, and an array of the team's last few results
   * (most recent first, padded with '-').
   */
  const teamsByGroup = $derived(
    groupdata.map((group) => ({
      groupName: group.groupName,
      teams: group.ranking.map((g: any) => {
        // O(1) team lookup via the indexed `getTeam` selector on the
        // teams store, instead of a per-row `Array.find` scan. We resolve
        // it here once so the TeamCard / Card components stay purely
        // presentational and don't have to reach into appState themselves.
        const teamRecord = appState.teams?.getTeam(g.contestantCode);

        /**
         * Last N results (most recent first), padded with '-' when a team
         * has played fewer than N games. `g.lastSix` is a string like
         * `'WDL'` (oldest → newest).
         */
        const results = (g.lastSix?.split('').reverse() || [])
          .concat(new Array(RECENT_RESULTS_COUNT).fill('-'))
          .slice(0, RECENT_RESULTS_COUNT);

        return {
          name: g.contestantName,
          code: g.contestantCode,
          rank: g.rank,
          points: g.points,
          qualified: teamRecord?.qualified || false,
          // Pre-resolved so TeamCard doesn't need appState.teams at all.
          slug: teamRecord?.slug,
          shortName: teamRecord?.shortName,
          results,
        };
      }),
    }))
  );
</script>

<!--
  Placeholder shown while a team row is off-screen. Mirrors the rendered
  `TeamCard` layout (rectangular flag + name/points + results badges).
  Per-row live-region suppressed because the group `<ol>` already labels
  the list.
-->
{#snippet placeholder()}
  <TeamCardPlaceholder label={null} />
{/snippet}

<Block width="widest">
  <ul class="mx-auto flex flex-wrap justify-center">
    <!-- One column per group -->
    {#each teamsByGroup as group (group.groupName)}
      <li class="column" in:fly|global={{ y: -5, duration: 500 }}>
        <h2 class="h4 mt-0">{group.groupName}</h2>

        <!-- Teams ordered by ranking -->
        <ol class="mb-0">
          {#each group.teams as team, i (team.code)}
            <li
              class="fmy-2"
              in:fly|global={{
                y: -15,
                duration: 500,
                delay: Math.min((i + 1) * 50, 1000),
              }}
            >
              <LazyMount height={TEAM_CARD_HEIGHT} {placeholder}>
                <TeamCard teamStats={team} link={true} />
              </LazyMount>
            </li>
          {/each}
        </ol>
      </li>
    {/each}
  </ul>
</Block>

<style lang="scss">
  @use '../layout.scss';
</style>
